import React, { useEffect, useMemo, useState } from "react";
import UsersTableHeaderFilters from "../Users/UsersTableHeaderFilters";
import UsersTableBody from "../Users/UsersTableBody";
import UsersTableFooterAndModals from "../Users/UsersTableFooterAndModals";
import MemberDto from "../../Models/MemberDto";
import { FadeTable } from "../Global/Transitions";
import { LoaderIcon } from "../Global/Icons";
import { useMsal } from "@azure/msal-react";
import { RevokeAccess } from "../../Services/AccessControlService";

type Props = {
  members: MemberDto[];
  pageSize?: number;
  userlimitinfo?: { currentUsers: number; maxUsers: number };
  initialShowModal?: boolean;
  highestPrivilegeRole?: string;
  allProjects: any[];
  projectsLoading?: boolean;
  onRemoveMember: (userId: string) => void;
  onAddMember: (member: MemberDto) => void;
  onSync: () => Promise<void>;
};

export default function UsersList({
  members = [],
  pageSize = 7,
  userlimitinfo,
  initialShowModal,
  highestPrivilegeRole,
  allProjects = [],
  projectsLoading = false,
  onRemoveMember,
  onAddMember,
  onSync,
}: Props) {
  const { instance, accounts } = useMsal();
  const [search, setSearch] = useState("");
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [selectedProjects, setSelectedProjects] = useState<string[]>([]);
  const [filtersEnabled, setFiltersEnabled] = useState(false);
  const [page, setPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState<Partial<MemberDto> | undefined>(undefined);
  const [userLimitInfo, setUserLimitInfo] = useState(() => userlimitinfo ?? null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
 

  useEffect(() => {
    if (!userlimitinfo) return;
    setUserLimitInfo({
      currentUsers: members.length,
      maxUsers: userlimitinfo.maxUsers,
    });
  }, [userlimitinfo, userlimitinfo?.maxUsers, members.length]);

  useEffect(() => {
    if (initialShowModal) {
      setEditingUser(undefined);
      setShowModal(true);
    }
  }, [initialShowModal]);

  const getProjectName = (id: string) => {
    const project = allProjects.find((p) => p.ticketno === id);
    const name = project?.projectName ?? "";
    return name || null;
  };

  const roleOptions = useMemo(() => {
    const map = new Map<string, string>();
    members.forEach((m) =>
      m.applicationRole.forEach((r) => {
        const key = (r.userRole ?? "").trim().toLowerCase();
        if (!map.has(key)) map.set(key, r.userRole);
      })
    );
    return Array.from(map.values());
  }, [members]);

  const projectOptions = useMemo(() => {
    const s = new Set<string>();
    members.forEach((m) => m.applicationRole.forEach((r) => r.projectId && s.add(r.projectId)));
    return Array.from(s.values());
  }, [members]);

  const toggleRole = (role: string) => {
    setSelectedRoles((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]
    );
    setPage(1);
  };

  const toggleProject = (project: string) => {
    setSelectedProjects((prev) =>
      prev.includes(project) ? prev.filter((p) => p !== project) : [...prev, project]
    );
    setPage(1);
  };

  const clearRoles = () => setSelectedRoles([]);
  const clearProjects = () => setSelectedProjects([]);
  const clearAllFilters = () => {
    setSelectedRoles([]);
    setSelectedProjects([]);
    setSearch("");
    setPage(1);
  };

  const visible = useMemo(() => {
    let filtered = members;
    if (search) {
      filtered = filtered.filter((m) =>
        m.displayName?.toLowerCase().includes(search.toLowerCase()) ||
        m.email?.toLowerCase().includes(search.toLowerCase())
      );
    }
    if (selectedRoles.length > 0) {
      filtered = filtered.filter((m) =>
        m.applicationRole.some((r) => selectedRoles.includes(r.userRole))
      );
    }
    if (selectedProjects.length > 0) {
      filtered = filtered.filter((m) =>
        m.applicationRole.some((r) => selectedProjects.includes(r.projectId))
      );
    }
    return filtered;
  }, [members, search, selectedRoles, selectedProjects]);

  const total = visible.length;
  const pagedVisible = useMemo(() => {
    const start = (page - 1) * pageSize;
    return visible.slice(start, start + pageSize);
  }, [visible, page, pageSize]);

  // FIXED: Instant optimistic delete — remove from UI immediately, then call API
  const handleRemove = async (userId: string) => {
    if (!userId) {
      setErrorMsg("User information missing.");
      return;
    }
    if (!accounts || accounts.length === 0) {
      setErrorMsg("You are not authenticated. Please sign in and try again.");
      return;
    }

    // Store user for potential rollback
    const removedUser = members.find(m => m.id === userId);
    
    // 1. INSTANTLY remove from UI
    onRemoveMember(userId);
    
    // 2. Call API in background
    try {
      await RevokeAccess(instance, accounts[0], userId);
      
    } catch (err: any) {
      // 3. FAILED — add user back to UI
      setErrorMsg(err?.message || "Failed to remove user. User has been restored.");
      if (removedUser) {
        onAddMember(removedUser);
      }
    }
  };

  const handleClose = () => setShowModal(false);

  const handleSaveUser = async (savedUser?: Partial<MemberDto>) => {
    setShowModal(false);
    setEditingUser(undefined);
    if (savedUser && savedUser.id && savedUser.email && savedUser.displayName) {
      onSync().catch(console.error);
    }
  };

  const getRoleClass = (role: string) => "";
  const hideClass = filtersEnabled ? "" : "visually-hidden";

  if (projectsLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: 200 }}>
        <LoaderIcon />
      </div>
    );
  }

  return (
    <>
     
      
        <FadeTable>
          <UsersTableHeaderFilters
            filtersEnabled={filtersEnabled}
            setFiltersEnabled={setFiltersEnabled}
            search={search}
            setSearch={setSearch}
            setPage={setPage}
            selectedRoles={selectedRoles}
            selectedProjects={selectedProjects}
            roleOptions={roleOptions}
            projectOptions={projectOptions}
            toggleRole={toggleRole}
            toggleProject={toggleProject}
            clearRoles={clearRoles}
            clearProjects={clearProjects}
            clearAllFilters={clearAllFilters}
            hideClass={hideClass}
            getRoleClass={getRoleClass}
            getProjectName={getProjectName}
            userLimitInfo={userLimitInfo}
          />
          <UsersTableBody
            visible={pagedVisible}
            accounts={accounts}
            highestPrivilegeRole={highestPrivilegeRole}
            allProjects={allProjects}
            handleRemove={handleRemove}
            setEditingUser={setEditingUser}
            setShowModal={setShowModal}
            page={page}
            pageSize={pageSize}
            total={total}
            setPage={setPage}
            addUserDisabled={userLimitInfo?.currentUsers === userLimitInfo?.maxUsers}
          />
        </FadeTable>
      
      {errorMsg && <div className="text-danger mt-3">{errorMsg}</div>}
      <UsersTableFooterAndModals
        showModal={showModal}
        handleClose={handleClose}
        handleSaveUser={handleSaveUser}
        editingUser={editingUser}
        existingEmails={members.map(u => u.email)}
        errorMsg={errorMsg}
        setErrorMsg={setErrorMsg}
        onAddMember={onAddMember}
        onSync={onSync}
      />
    </>
  );
}


